import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectItem,
  Textarea,
} from "@porchpass/ui";
import type { DomainTaskStatusType } from "@porchpass/api-client";
import { updateCondition } from "src/api/preApproval";
import type { PreApprovalCondition } from "src/api/preApproval/types";
import { formatStatusLabel } from "../../utils";
import { CONDITION_STATUSES } from "./conditionStatusOptions";

interface EditConditionModalProps {
  condition: PreApprovalCondition;
  onCancel: () => void;
  onSuccess: (condition: PreApprovalCondition) => void;
}

export function EditConditionModal({
  condition,
  onCancel,
  onSuccess,
}: EditConditionModalProps) {
  const { applicationId } = useParams<{ applicationId: string }>();
  const [title, setTitle] = useState(condition.title);
  const [internalDescription, setInternalDescription] = useState(
    condition.internal_description ?? "",
  );
  const [externalDescription, setExternalDescription] = useState(
    condition.external_description ?? "",
  );
  const [status, setStatus] = useState<DomainTaskStatusType>(
    condition.status ?? "not_done",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!applicationId) {
      return;
    }

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("Enter a condition title.");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const updated = await updateCondition(applicationId, condition, {
        title: trimmedTitle,
        internal_description: internalDescription.trim(),
        external_description: externalDescription.trim(),
        status,
      });
      onSuccess(updated);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Unable to update condition.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit condition</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <form
            id="edit-condition-form"
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
            className="flex flex-col gap-4"
          >
            <Input
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              clearable={false}
            />
            <Textarea
              label="Internal description"
              value={internalDescription}
              onChange={(event) => setInternalDescription(event.target.value)}
              rows={3}
              showOptional
            />
            <Textarea
              label="External description"
              value={externalDescription}
              onChange={(event) => setExternalDescription(event.target.value)}
              rows={3}
              showOptional
            />
            <Select
              label="Status"
              value={status}
              onValueChange={(value) =>
                setStatus(value as DomainTaskStatusType)
              }
            >
              {CONDITION_STATUSES.map((option) => (
                <SelectItem key={option} value={option}>
                  {formatStatusLabel(option)}
                </SelectItem>
              ))}
            </Select>

            {error && <p className="text-sm text-[#B91C1C]">{error}</p>}
          </form>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-condition-form"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
